import { css } from 'styled-components';

import { CommonTheme, ManualCSSTheme } from 'app/shared/theme';
import { generateCSSPropForScreenSize } from 'app/shared/components/styleUtils/generateCSSPropForScreenSize';

type SpacingProp = number | string;

export interface WithSpacingProps {
  // global margin & padding
  m?: SpacingProp | SpacingProp[];
  p?: SpacingProp | SpacingProp[];

  // horizontal margin & padding
  mx?: SpacingProp | SpacingProp[];
  px?: SpacingProp | SpacingProp[];

  // vertical margin & padding
  my?: SpacingProp | SpacingProp[];
  py?: SpacingProp | SpacingProp[];

  // margin top, right, bottom, left
  mt?: SpacingProp | SpacingProp[];
  mr?: SpacingProp | SpacingProp[];
  mb?: SpacingProp | SpacingProp[];
  ml?: SpacingProp | SpacingProp[];

  // padding top, right, bottom, left
  pt?: SpacingProp | SpacingProp[];
  pr?: SpacingProp | SpacingProp[];
  pb?: SpacingProp | SpacingProp[];
  pl?: SpacingProp | SpacingProp[];
}

export interface WithSpacingCSSProps extends WithSpacingProps {
  theme: CommonTheme | ManualCSSTheme;
}

function generateCssProperty(
  theme: CommonTheme | ManualCSSTheme,
  spacingPropName: string,
  value: string | number
) {
  const typeMapping: { [key: string]: string } = {
    m: 'margin',
    p: 'padding',
  };

  const [type, sideOrAxis] = spacingPropName.split('');

  const sideOrAxisMapping: { [key: string]: string } = {
    t: 'top',
    r: 'right',
    b: 'bottom',
    l: 'left',
    x: 'inline',
    y: 'block',
  };

  const cssProp = sideOrAxisMapping[sideOrAxis]
    ? `${typeMapping[type]}-${sideOrAxisMapping[sideOrAxis]}`
    : `${typeMapping[type]}`;

  if (typeof value === 'number') {
    return `${cssProp}: ${theme.ruler[value]}px;`;
  }

  return `${cssProp}: ${value};`;
}

function generateCSSForScreenSize(
  screenSize: string,
  { theme, ...withSpacingProps }: WithSpacingCSSProps
) {
  let s = '';

  Object.keys(withSpacingProps).forEach((spacingPropName) => {
    if (/^(m|p)(t|b|r|l|x|y)?$/.test(spacingPropName)) {
      s += generateCSSPropForScreenSize({
        theme,
        screenSize,
        propName: spacingPropName,
        value: withSpacingProps[spacingPropName as keyof WithSpacingProps],
        generateCssProperty,
      });
    }
  });

  return css`${s}`;
}

export const withSpacingCSS = (props: WithSpacingCSSProps) => {
  const { theme } = props;
  let styles: any = [];

  Object.keys(theme.media).forEach((breakPointName) => {
    const generatedCSS = generateCSSForScreenSize(breakPointName, props);

    if (generatedCSS.length > 0) {
      const mediaQuery = theme.utils.getMediaQuery(breakPointName);
      styles.push(mediaQuery`${generatedCSS}`);
    }
  });

  return styles;
};
